import React, { useState } from 'react';
import { mockDemographicsDatasets, mockTravelSurveys, mockUtilitySummary } from '../../../data/mockScenarioData';

/** @param {'demographics'|'travel'|'all'} section - when set, only that section is rendered (for workflow dedicated pages) */
const HumanTab = ({ data, updateData, utilityName, region, section = 'all' }) => {
  const showDemographics = section === 'all' || section === 'demographics';
  const showTravel = section === 'all' || section === 'travel';
  const [selectedDemographics, setSelectedDemographics] = useState(data.demographicsDatasetId || null);
  const [selectedTravelSurvey, setSelectedTravelSurvey] = useState(data.travelSurveyId || null);
  const [showDemoSummary, setShowDemoSummary] = useState(false);
  const [showTravelSummary, setShowTravelSummary] = useState(false);

  const handleDemographicsSelection = (datasetId) => {
    setSelectedDemographics(datasetId);
    setShowDemoSummary(true);
    updateData({ ...data, demographicsDatasetId: datasetId });
  };

  const handleTravelSurveySelection = (surveyId) => {
    setSelectedTravelSurvey(surveyId);
    setShowTravelSummary(true);
    updateData({ ...data, travelSurveyId: surveyId });
  };

  const selectedDemoDataset = mockDemographicsDatasets.find(d => d.id === selectedDemographics);
  const selectedTravelData = mockTravelSurveys.find(s => s.id === selectedTravelSurvey);
  const demoSummary = mockUtilitySummary.demographics;
  const travelSummary = mockUtilitySummary.travel;

  return (
    <div className="space-y-6">
      {(showDemographics || showTravel) && (
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <div className="mb-3">
          <h3 className="text-xl font-bold text-gray-800 font-ui">Human Factors & Behavior</h3>
        </div>
        <p className="text-sm text-gray-700">
          Configure demographic data and travel patterns that influence EV adoption rates and charging demand. 
          Specify data sources and view summaries for the utility service territory.
        </p>
      </div>
      )}

      {/* Demographics Section */}
      {showDemographics && (
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 font-ui">
          Demographics
        </h4>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Census Dataset
          </label>
          <select
            value={selectedDemographics || ''}
            onChange={(e) => handleDemographicsSelection(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
          >
            <option value="">Choose demographics dataset...</option>
            {mockDemographicsDatasets.map(dataset => (
              <option key={dataset.id} value={dataset.id}>
                {dataset.name} - {dataset.geography}
              </option>
            ))}
          </select>
        </div>

        {selectedDemoDataset && (
          <>
            {/* Dataset Info */}
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-4">
              <h5 className="font-semibold text-gray-800 mb-2">Dataset Information</h5>
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div>
                  <p className="text-gray-600">Source</p>
                  <p className="font-semibold text-gray-800">{selectedDemoDataset.source}</p>
                </div>
                <div>
                  <p className="text-gray-600">Year</p>
                  <p className="font-semibold text-gray-800">{selectedDemoDataset.year}</p>
                </div>
                <div>
                  <p className="text-gray-600">Geography Level</p>
                  <p className="font-semibold text-gray-800">{selectedDemoDataset.geography}</p>
                </div>
                <div>
                  <p className="text-gray-600">Coverage</p>
                  <p className="font-semibold text-gray-800">{selectedDemoDataset.coverage}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">
                  <span className="font-semibold">Variables:</span> {selectedDemoDataset.variables.join(', ')}
                </p>
                <p className="text-xs text-gray-500">License: {selectedDemoDataset.license}</p>
              </div>
            </div>

            {/* Demographics Summary */}
            {showDemoSummary && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h5 className="font-semibold text-gray-800 mb-4">
                  Summary: {utilityName} Service Territory
                </h5>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3 border border-gray-300">
                    <div className="mb-2">
                      <p className="text-xs text-gray-600">Population</p>
                    </div>
                    <p className="text-xl font-bold text-gray-800">
                      {demoSummary.population.toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-gray-300">
                    <div className="mb-2">
                      <p className="text-xs text-gray-600">Households</p>
                    </div>
                    <p className="text-xl font-bold text-gray-800">
                      {demoSummary.households.toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-gray-300">
                    <div className="mb-2">
                      <p className="text-xs text-gray-600">Median Income</p>
                    </div>
                    <p className="text-xl font-bold text-gray-800">
                      ${(demoSummary.medianIncome / 1000).toFixed(0)}K
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-gray-300">
                    <div className="mb-2">
                      <p className="text-xs text-gray-600">Vehicles/HH</p>
                    </div>
                    <p className="text-xl font-bold text-gray-800">
                      {demoSummary.vehiclesPerHousehold}
                    </p>
                  </div>
                </div>

                {/* Housing Types */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Housing Types</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <p className="text-xs text-gray-600">Single Family</p>
                      <p className="text-lg font-bold text-gray-800">{demoSummary.housingTypes.singleFamily}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Multi Family</p>
                      <p className="text-lg font-bold text-gray-800">{demoSummary.housingTypes.multiFamily}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Other</p>
                      <p className="text-lg font-bold text-gray-800">{demoSummary.housingTypes.other}%</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Travel Surveys Section */}
      {showTravel && (
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 font-ui">
          Travel Surveys (NHTS)
        </h4>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Travel Survey Dataset
          </label>
          <select
            value={selectedTravelSurvey || ''}
            onChange={(e) => handleTravelSurveySelection(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
          >
            <option value="">Choose travel survey...</option>
            {mockTravelSurveys.map(survey => (
              <option key={survey.id} value={survey.id}>
                {survey.name} - {survey.coverage}
              </option>
            ))}
          </select>
        </div>

        {selectedTravelData && (
          <>
            {/* Dataset Info */}
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-4">
              <h5 className="font-semibold text-gray-800 mb-2">Dataset Information</h5>
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div>
                  <p className="text-gray-600">Source</p>
                  <p className="font-semibold text-gray-800">{selectedTravelData.source}</p>
                </div>
                <div>
                  <p className="text-gray-600">Year</p>
                  <p className="font-semibold text-gray-800">{selectedTravelData.year}</p>
                </div>
                <div>
                  <p className="text-gray-600">Sample Size</p>
                  <p className="font-semibold text-gray-800">{selectedTravelData.sampleSize}</p>
                </div>
                <div>
                  <p className="text-gray-600">Coverage</p>
                  <p className="font-semibold text-gray-800">{selectedTravelData.coverage}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">
                  <span className="font-semibold">Metrics:</span> {selectedTravelData.metrics.join(', ')}
                </p>
                <p className="text-xs text-gray-500">License: {selectedTravelData.license}</p>
              </div>
            </div>

            {/* Travel Summary */}
            {showTravelSummary && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h5 className="font-semibold text-gray-800 mb-4">
                  Travel Patterns: {utilityName} Service Territory
                </h5>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-300">
                    <div className="mb-3">
                      <h6 className="font-semibold text-gray-800">Trip Characteristics</h6>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Avg Daily Trips</span>
                        <span className="font-bold text-gray-800">{travelSummary.avgDailyTrips}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Avg Trip Length</span>
                        <span className="font-bold text-gray-800">{travelSummary.avgTripLength} mi</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-300">
                    <div className="mb-3">
                      <h6 className="font-semibold text-gray-800">Peak Travel Times</h6>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">Morning Departure</span>
                        <p className="font-bold text-gray-800">{travelSummary.peakDeparture}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Evening Return</span>
                        <p className="font-bold text-gray-800">{travelSummary.peakReturn}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-gray-700">
                  <span className="font-semibold">Impact on Charging:</span> Peak departure and return times 
                  drive residential charging demand patterns. Average trip length informs range requirements 
                  and charging frequency.
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Additional Options - only when showing both sections */}
      {section === 'all' && (
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 font-ui">
          Behavioral Assumptions
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              EV Adoption Likelihood by Income Bracket
            </label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500">
              <option>Default - Income-weighted adoption curve</option>
              <option>Aggressive - Accelerated adoption across all brackets</option>
              <option>Conservative - Slower adoption in lower brackets</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Multi-Family Charging Access
            </label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500">
              <option>Low - 20% have access to dedicated charging</option>
              <option selected>Medium - 50% have access to dedicated charging</option>
              <option>High - 80% have access to dedicated charging</option>
            </select>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default HumanTab;
